import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import firebase from 'firebase';
import { AngularFireDatabase } from 'angularfire2/database';
import { AngularFirestore } from 'angularfire2/firestore';
import { ENV } from '@app/env';
import { switchMap } from 'rxjs/operators';
import { sortBy, toArray } from 'lodash';
import { UtilitiesProvider } from './utilities';
import { IReserveShoppingCart, EntityDataType } from 'models/shopping-cart.model';
import { EntityType } from 'models/reserve.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import moment from 'moment';
export interface IEntityObject {
  type: EntityType;
  eventId: string;
}

@Injectable()
export class ReserveProvider {

  constructor(
    private afdb: AngularFireDatabase,
    private utilities: UtilitiesProvider,
    private afStore: AngularFirestore,
    private http: HttpClient
  ) { }

  /**
   * Consulta una la reserva de una entidad asignada
   *
   * @param entity
   * @param entity_id
   * @param event_id
   */
  public getReserveInformation(entity: string, entity_id: string, event_id: string): Observable<any> {
    switch (entity) {
      case 'CommercialSamples':
      case 'Staff':
        if (this.utilities.validateEventIdParam(event_id)) {
          return this.afdb.object(`Clients/${ENV.client}/Events/${event_id}/${entity}/${entity_id}`).valueChanges()
            .pipe(
              switchMap((data: any) => {
                if (!data) return Observable.of({})
                return this.afdb.object(`/Clients/${ENV.client}/Reserves/${data.reserve}`).valueChanges();
              })
            );
        } else {
          const endpoint = entity === 'CommercialSamples' ? 'Clients' : 'ClientList';
          return this.afdb.object(`${endpoint}/${ENV.client}/${entity}/${entity_id}`).valueChanges()
            .pipe(
              switchMap((data: any) => {
                if (!data) return Observable.of({})
                return this.afdb.object(`/Clients/${ENV.client}/Reserves/${data.reserve}`).valueChanges();
              })
            );
        }
      case 'Event':
        return this.afdb.object(`Clients/${ENV.client}/EventList/${entity_id}`).valueChanges()
          .pipe(
            switchMap((data: any) => {
              if (!data) return Observable.of({})
              return this.afdb.object(`/Clients/${ENV.client}/Reserves/${data.reserve}`).valueChanges();
            })
          );
    };
  }

  get defaultTemplateNotificationCancelReserve() {
    // tslint:disable-next-line:max-line-length
    return "https://firebasestorage.googleapis.com/v0/b/rebus-v4-prod.appspot.com/o/Mails%2FtemplateNotification_cancel_reserve.html?alt=media&token=89bb96bf-707a-451d-b135-2beee93a473b";
  }

  public getReserveInformationRef(reserveId: string) {
    return this.afdb.object(`/Clients/${ENV.client}/Reserves/${reserveId}`).query.ref;
  }

  public getIdPaymentHistory(userId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/UserPaymentHistory/${userId}`).valueChanges()
  }

  /**
   * Retorna todas las reservas de un usuario especifico
   * @param uid
   */
  public getUserReservesRef(uid: string) {
    return this.afdb.object(`/Clients/${ENV.client}/UserReserves/${uid}`)
      .query.ref;
  }

  getReserverPerId(userId, reserveId) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/UserReserves/${userId}/${reserveId}`)
      .valueChanges();
  }

  public getServicesUserReserve(idService) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/AdditionalServices/${idService}`)
      .valueChanges();
  }

  public getCodesReserveUserByEvent(clientId, eventId) {
    return this.afdb.object(`/Clients/${clientId}/ReservePrivacyPrivate/${eventId}`)
      .query.ref;
  }

  /**
   * Retorna la informacion de una reserva especifica
   *
   * @param userId
   * @param reserveId
   */
  public getReservePerId(userId: string, reserveId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/UserReserves/${userId}/${reserveId}`).valueChanges();
  }

  validateReserveRef(userId, reserveId) {
    return this.afdb.object(`/Clients/${ENV.client}/UserReserves/${userId}/${reserveId}`).query.ref;
  }

  setUserEntryReserve(userId, reserveId: string) {
    return this.afdb.object(`/Clients/${ENV.client}/UserReserves/${userId}/${reserveId}/entry`).set(true);
  }

  /**
   * Calcula la entidad requerida para iniciar el proceso de consulta en una reserva
   *
   * @param entity
   * @param entity_id
   * @param event_id
   */
  public getEntityDataRef(entity: string, entity_id: string, event_id: string) {
    switch (entity) {
      case 'CommercialSamples':
      case 'Staff':
        if (this.utilities.validateEventIdParam(event_id)) {
          return this.afdb.object(`Clients/${ENV.client}/Events/${event_id}/${entity}/${entity_id}`).query.ref;
        } else {
          const endpoint = entity === 'CommercialSamples' ? 'Clients' : 'ClientList';
          return this.afdb.object(`${endpoint}/${ENV.client}/${entity}/${entity_id}`)
            .query.ref;
        }
      case 'Event':
        return this.afdb.object(`Clients/${ENV.client}/EventList/${entity_id}`)
          .query.ref;
    };
  }

  public getEntityData(entity: string, entity_id: string, event_id: string) {
    switch (entity) {
      case 'CommercialSample':
      case 'Staff':
        if (this.utilities.validateEventIdParam(event_id)) {
          if (entity === 'CommercialSample') {
            return this.afdb.object(`Clients/${ENV.client}/Events/${event_id}/CommercialSamples/${entity_id}`)
              .valueChanges();
          } else {
            return this.afdb.object(`Clients/${ENV.client}/Events/${event_id}/${entity}/${entity_id}`)
              .valueChanges();
          }
        } else {
          const endpoint = entity === 'CommercialSample' ? 'Clients' : 'ClientList';
          const path = entity === 'CommercialSample' ? 'CommercialSamples' : 'Staff';
          return this.afdb.object(`${endpoint}/${ENV.client}/${path}/${entity_id}`)
            .valueChanges();
        }
      case 'Event':
        return this.afdb.object(`Clients/${ENV.client}/EventList/${entity_id}`)
          .valueChanges();
    };
  }

  public getReserveData(reserve_id: string) {
    return this.afdb.object(`/Clients/${ENV.client}/Reserves/${reserve_id}`).valueChanges();
  }

  public getPrivateEventAccessCode(reserveId: string, code: string) {
    return this.afStore.collection(`/${ENV.client}/reserve_private/${reserveId}`)
      .ref
      .where('code', '==', code)
      .limit(1)
      .get();
  }

  public setPrivateEventAccessCode(reserveId: string, codeId: string, newQuota: number) {
    return this.afStore.collection(`/${ENV.client}/reserve_private/${reserveId}`).doc(codeId).update({ quota: newQuota });

  }

  public getMyReservesList(userId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/UserReserves/${userId}`).valueChanges();
  }

  // public setLastDateReserveUser(iduser, currentDate) {
  //   return this.afdb.object(`/Clients/${ENV.client}/Users/${iduser}/lastDateReserve`).set(currentDate);
  // }

  public getCalendarByStaff(calendarId: string, staffId: string) {
    return this.afdb.object<any>(`/ClientList/${ENV.client}/Staff/${staffId}/Reserve/calendars/${calendarId}`).valueChanges();
  }

  public getCalendarByComercialSample(calendarId: string, standId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/CommercialSamples/${standId}/Reserve/calendars/${calendarId}`).valueChanges();
  }

  public getCalendarEventByStaff(calendarId: string, staffId: string, eventId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/Events/${eventId}/Staff/${staffId}/Reserve/calendars/${calendarId}`).valueChanges();
  }

  public getCalendarEventByComercialSample(calendarId: string, standId: string, eventId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/Events/${eventId}/CommercialSamples/${standId}/Reserve/calendars/${calendarId}`).valueChanges();
  }

  public getReservesByUser(userId: string) {
    return this.afdb.list<any>(`/Clients/${ENV.client}/UserReserves/${userId}`).valueChanges();
  }

  public getReservesByUserRef(userId: string) {
    return this.afdb.object<any>(`/Clients/${ENV.client}/UserReserves/${userId}`).query;
  }
  /**
   * @author Jhonny Martínez
   * Modified to get a query instead of an observable
   */
  public async getMyReserves(userId: string) {
    const userReservesObj = await this.afdb
      .list(`/Clients/${ENV.client}/UserReserves/${userId}`)
      .query
      .once('value')
      .then(snap => snap.val());
    const userReservesList = toArray(userReservesObj);
    const reserves = await Promise.all<any>(userReservesList.map(el => {
      return this.getReserveInformationRef(el.reserveId)
        .once('value')
        .then((snap) => snap.val());
    }));
    this.utilities.consoleDecorator('New data: ', reserves)
    let dataMapped = userReservesList.map((item, i) => {
      if (reserves[i].isCalendar) {
        return {
          ...item,
          ...reserves[i],
          calendarInformation: reserves[i]['calendar'][item.calendarId]
        };
      }
      return {
        ...item,
        ...reserves[i]
      };
    })
    dataMapped = sortBy(dataMapped, [(o) => { return o.created_at; }]);
    this.utilities.consoleDecorator('Data Maped', dataMapped);
    return dataMapped;
  }

  public createReserve(data: EntityDataType, entity: IEntityObject, currentCart?: IReserveShoppingCart, idCodePrivateUser = null): IReserveShoppingCart {
    const reserveData = {
      type: entity.type,
      eventId: entity.type === "Event" ? null : entity.eventId,
      id: data.id,
      idCodePrivateUser
    };
    let cart: IReserveShoppingCart = {
      created_at: Date.now(),
      isLoad: true,
      reserves: { [reserveData.id]: reserveData },
      state: 'pending',
    };
    if (currentCart) {
      cart = {
        ...currentCart,
        isLoad: true,
        reserves: { ...cart.reserves, ...currentCart.reserves }
      };
    }
    return cart;
  }

  public addReserveToShoppingCart(data: IReserveShoppingCart, uid: string) {
    return this.afdb.object(`/Clients/${ENV.client}/UserReserveShoppingCart/${uid}`)
      .update(data);
  }

  public addFormToReserve(userId: string, sale_form_user_data: any, reserveId: string): Promise<any> {
    return this.afdb.object(`/Clients/${ENV.client}/UserReserveShoppingCart/${userId}/reserves/${reserveId}`)
      .update({ sale_form_user_data });
  }

  public getReserveShoppingCartSnap(uid: string) {
    return this.afdb.object(`/Clients/${ENV.client}/UserReserveShoppingCart/${uid}`)
      .query.once('value').then(snap => snap.val());
  }

  public getUserReservesSnapByEntity(uid: string, id: string) {
    return this.afdb.list(
      `/Clients/${ENV.client}/UserReserves/${uid}`,
      ref => ref.orderByChild('idObject').equalTo(id)
    ).query.once('value').then(snap => snap.val());
  }

  public getUserReservesByEntity(uid: string, id: string) {
    return this.afdb.list(
      `/Clients/${ENV.client}/UserReserves/${uid}`,
      ref => ref.orderByChild('idObject').equalTo(id)
    ).valueChanges();
  }

  public sendEmailNotificationCancelReserve(
    emailUser,
    emailNotification,
    nameReserve,
    userName,
    template = this.defaultTemplateNotificationCancelReserve
  ) {
    const timeCancelReserve = moment(firebase.database.ServerValue.TIMESTAMP).format('MMMM Do YYYY, h:mm:ss a');
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json');
    return this.http.post(`${ENV.backend}/notification/mail`,
      {
        "params": {
          "nameReserve": nameReserve,
          "userName": userName,
          "emailUser": emailUser,
          "timeCancelReserve": timeCancelReserve
        },
        "template": template,
        "to": [emailUser, emailNotification],
        "subjet": "Notificación Cancelación reserva",
        "from": "noreply@rebus.com.co"
      }
      , {
        headers: headers
      });
  }

}



// WEBPACK FOOTER //
// ./src/providers/reserves.ts